import React, { useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Col, Row, Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import Calendar from "components/common/Calendar";
import moment from "moment";

const PreauthResponseService = ({ show, row }) => {
  const { viewPreauthorizationInfo } = useSelector(
    (state) => state.preauthorization,
  );

  const isAdvanced = viewPreauthorizationInfo?.preauthadv_id;

  const diagnosis = viewPreauthorizationInfo?.provider_request?.diagnosis;

  const getStatusInfo = (status) => {
    let statusValue = "";
    let statusStyle = { backgroundColor: "#f8f9fa", color: "#000" };

    const rawStatus = status || viewPreauthorizationInfo?.preauth_status;

    if (rawStatus) {
      statusValue = String(rawStatus).toLowerCase();
    } else {
      statusValue = "";
    }

    // Map status to corresponding styles
    const statusStyles = {
      error: { backgroundColor: "#fee2e2", color: "#dc2626" },
      rejected: { backgroundColor: "#fee2e2", color: "#dc2626" },
      noresp: { backgroundColor: "#e5e7eb", color: "#374151" },
      approved: { backgroundColor: "#dcfce7", color: "#16a34a" },
      complete: { backgroundColor: "#dcfce7", color: "#16a34a" },
      cancelled: { backgroundColor: "#fce7f6", color: "#db2777" },
      pended: { backgroundColor: "#dbeafe", color: "#2563eb" },
      pending: { backgroundColor: "#dbeafe", color: "#2563eb" },
      queued: { backgroundColor: "#fef9c3", color: "#ca8a04" },
      partial: { backgroundColor: "#cffafe", color: "#0891b2" },
      adjudication: { backgroundColor: "#ede9fe", color: "#7c3aed" },
      "not-required": { backgroundColor: "#e0f2fe", color: "#0284c7" },
    };

    if (statusValue && statusStyles[statusValue]) {
      statusStyle = statusStyles[statusValue];
    }

    return { statusValue, statusStyle };
  };

  // Render status badge using getStatusInfo
  const renderStatusBadge = useCallback(
    (status) => {
      const { statusValue, statusStyle } = getStatusInfo(status);
      return (
        <div
          className="d-inline-block rounded-pill px-2 py-1"
          style={{
            backgroundColor: statusStyle.backgroundColor,
            color: statusStyle.color,
            fontSize: "0.75rem",
            fontWeight: 500,
            border: "1px solid transparent",
            borderColor: "rgba(0,0,0,0.1)",
          }}
        >
          {statusValue.toUpperCase()}
        </div>
      );
    },
    [show, row, viewPreauthorizationInfo],
  );
  const formatServiceDate = (dateStr) => {
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr))
      return { day: "", month: "", year: "" };

    const date = moment(dateStr, "YYYY-MM-DD");
    if (!date.isValid()) return { day: "", month: "", year: "" };

    console.log({ date: date.format("DD") });

    return {
      day: date.format("DD"),
      month: date.format("MMM"),
      year: date.format("YYYY"),
    };
  };
  // Render service items
  const renderServiceItems = useMemo(() => {
    if (!viewPreauthorizationInfo?.service_procedures?.length) return null;

    return viewPreauthorizationInfo?.service_procedures.map((item, index) => {
      const { day, month, year } = formatServiceDate(item.dateserv);

      const diagnosisInformation = diagnosis?.find(
        (i) => i?.diagonis_code === item?.servinfo_diag,
      );

      const dosageInstructionInfo =
        item?.medication_request?.dosage_instruction;

      const hasPrescriptionDetails =
        item.presmed ||
        item.pharslrsn ||
        item.pharsubs ||
        item.pharstrnth ||
        item.pharscabrsn ||
        item?.pharscabrsn_oth ||
        (dosageInstructionInfo?.period_start &&
          dosageInstructionInfo?.period_end) ||
        dosageInstructionInfo?.duration ||
        dosageInstructionInfo?.frequency ||
        dosageInstructionInfo?.dose ||
        dosageInstructionInfo?.dosage_instruction ||
        dosageInstructionInfo?.refill ||
        dosageInstructionInfo?.patient_instruction ||
        dosageInstructionInfo?.time_instruction_desc ||
        dosageInstructionInfo?.route_desc;
      return (
        <Col xs={12} key={item.id} className="mb-3">
          <Card className="p-3 h-100">
            <Row className="g-3">
              <Col xs={12} md={2} className="text-center">
                <div className="badge badge-subtle-success fs-10 mb-3">
                  {index + 1}
                </div>
                {item.dateserv && (
                  <div className="d-flex justify-content-center mt-2">
                    <Calendar month={month} day={day} year={year} />
                  </div>
                )}
              </Col>
              <Col xs={12} md={10}>
                <Row>
                  <Col
                    xs={12}
                    className="d-flex flex-wrap justify-content-between gap-2 mb-2"
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="badge badge-subtle-success p-2">
                        {item.itemcode}
                      </div>
                      <div className="fs-10">{item.itemdescp}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="badge badge-subtle-warning p-2">
                        {item.itemlclcode}
                      </div>
                      <div className="fs-10">{item.itemlcldescp}</div>
                    </div>
                    {renderStatusBadge(item.status)}
                  </Col>

                  <Col xs={12}>
                    <Row className="gy-2">
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">Type</span>
                          <span className="fs-10 text-600">{item.prdtyp}</span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Unit Price
                          </span>
                          <span className="fs-10 text-600 text-warning">
                            {item.servinfo_prdpri}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Approved Quantity
                          </span>
                          <span className="fs-10 text-600">
                            {item.apprv_qty || 0}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Patient Share
                          </span>
                          <span className="fs-10 text-600 text-success">
                            {item.prdpshre}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Eligible Amount
                          </span>
                          <span className="fs-10 text-600 text-primary">
                            {item.elig_amnt || 0}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Benefit Amount
                          </span>
                          <span className="fs-10 text-600 text-primary">
                            {item.benf_amnt || 0}
                          </span>
                        </div>
                      </Col>
                      {item.invoiceNo && item.invoiceNo !== "-" && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Invoice No
                            </span>
                            <span className="fs-10 text-600 text-info">
                              {item.invoiceNo}
                            </span>
                          </div>
                        </Col>
                      )}
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Tooth No
                          </span>
                          <span className="fs-10 text-600 text-danger">
                            {item.toothno}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Diagnosis
                          </span>
                          <span className="fs-10 text-600">
                            {diagnosisInformation?.diagonis_code ? `(${diagnosisInformation?.diagonis_code || ''}) - ${diagnosisInformation?.diagonis_description || ''}` : diagnosisInformation?.diagonis_description || ''}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Practitioner
                          </span>
                          <span className="fs-10 text-600">
                            {item.pract_name}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Is Maternity
                          </span>
                          <span className="fs-10 text-600">{item.matrnty}</span>
                        </div>
                      </Col>
                      {item.bodysite && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Body Site
                            </span>
                            <span className="fs-10 text-600">
                              {item.bodysitedesc}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.subsite && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Sub Site
                            </span>
                            <span className="fs-10 text-600">
                              {item.subsitedesc}
                            </span>
                          </div>
                        </Col>
                      )}
                      {hasPrescriptionDetails && (
                        <div className="fs-9 text-success fw-bold mb-2 border-bottom py-2">
                          Prescription Details
                        </div>
                      )}
                      {item.presmed && (
                        <Col xs={6} md={2}>
                          <div className="d-flex  flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Prescribed Medication Code
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.presmed}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.claimMedicalDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Prescribed Medication Desc.
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.claimMedicalDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.pharstrnth && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Strength
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.pharstrnth}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.pharscabrsn && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Absence Reason
                            </span>
                            <span className="fs-10 text-capitalize text-info-emphasis">
                              {item.pharscabrsn}
                              {item?.pharscabrsn_oth && (
                                <div>({item?.pharscabrsn_oth})</div>
                              )}
                            </span>
                          </div>
                        </Col>
                      )}

                      {item.pharslrsn && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Selection Reason
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {item.pharslrsn}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.pharsubs && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Pharmacist Substitute
                            </span>
                            <div className="d-flex flex-column">
                              <span className="fs-10 text-info-emphasis">
                                {item.pharsubs}
                              </span>
                              {item.pharscabrsn && item.pharscabrsn != "-" && (
                                <span className="fs-10 text-info-emphasis">
                                  ({item.pharscabrsn})
                                </span>
                              )}
                            </div>
                          </div>
                        </Col>
                      )}

                      {dosageInstructionInfo?.period_start &&
                        dosageInstructionInfo?.period_end && (
                          <Col xs={6} md={2}>
                            <div className="d-flex flex-column">
                              <span className="fs-10 text-info fw-bold">
                                Period
                              </span>
                              <span className="fs-10 text-info-emphasis">
                                {`${dosageInstructionInfo?.period_start} - ${dosageInstructionInfo?.period_end}`}
                              </span>
                            </div>
                          </Col>
                        )}
                      {dosageInstructionInfo?.duration && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Duration
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstructionInfo?.duration}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstructionInfo?.frequency && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Frequency
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstructionInfo?.frequency}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstructionInfo?.dose && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Dose
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {`${dosageInstructionInfo?.dose}`}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstructionInfo?.route_desc && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Route
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstructionInfo?.route_desc}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstructionInfo?.time_instruction_desc && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Time Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstructionInfo?.time_instruction_desc}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstructionInfo?.refill && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Refill
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstructionInfo?.refill}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstructionInfo?.text && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Dosage Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstructionInfo?.text}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstructionInfo?.patient_instruction && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Patient Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstructionInfo?.patient_instruction}
                            </span>
                          </div>
                        </Col>
                      )}

                      {(item?.disposition ||
                        item?.adjreason?.length > 0 ||
                        item?.reasoncode ||
                        item?.process_note?.length > 0) && (
                        <Col xs={12}>
                          <div className="d-flex flex-column">
                            {item?.disposition && (
                              <span className="fs-10 text-danger">
                                {`(Disposition)`} {item?.disposition || ""}
                              </span>
                            )}

                            {item?.process_note?.map((note, nIdx) => (
                              <span className="fs-10 text-danger" key={nIdx}>
                                {`(Process Note)`} {note?.text || ""}
                              </span>
                            ))}

                            {item?.reasoncode && (
                              <span className="fs-10 text-danger">
                                {`(Adjudication)`}{" "}
                                {`(${item?.reasoncode}) - ${item?.reasondesc}` ||
                                  ""}
                              </span>
                            )}
                            {item?.adjreason?.length > 0 &&
                              item?.adjreason?.map((adj, aIdx) => (
                                <span className="fs-10 text-danger" key={aIdx}>
                                  {`(Adjudication)`}{" "}
                                  {`(${adj?.code}) - ${adj?.name}` || ""}
                                </span>
                              ))}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
      );
    });
  }, [renderStatusBadge]);

  // Render advanced items
  const renderAdvancedItems = useMemo(() => {
    if (!isAdvanced || !viewPreauthorizationInfo?.service_procedures?.length)
      return null;

    return viewPreauthorizationInfo?.service_procedures?.map((item, index) => (
      <Col xs={12} key={index} className="mb-3">
        <Card className="p-3 h-100">
          <Row className="g-3">
            <Col xs={12} md={2} className="text-center">
              <div className="category-sevie badge-subtle-success text-success fs-10">
                {index + 1}
              </div>
            </Col>
            <Col xs={12} md={10}>
              <Row>
                <Col
                  xs={8}
                  className="d-flex flex-wrap justify-content-between gap-2 mb-2"
                >
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="badge badge-subtle-success p-2">
                      {item?.itemcode || ""}
                    </div>
                    <div className="fs-10">{item?.itemdescp || ""}</div>
                  </div>
                </Col>
                <Col xs={2}>
                  <div className="d-flex flex-column">
                    <span className="fs-10 text-800 fw-bold">
                      Approved Quantity
                    </span>
                    <span className="fs-10 fw-bold text-success">
                      {item?.apprv_qty || 0}
                    </span>
                  </div>
                </Col>
                <Col xs={2}>{renderStatusBadge(item?.serv_status)}</Col>
              </Row>
            </Col>
          </Row>
        </Card>
      </Col>
    ));
  }, [isAdvanced, renderStatusBadge]);

  //   // Render diagnosis section
  const renderDiagnosisSection = useMemo(() => {
    if (!isAdvanced || !diagnosis?.length) return null;

    return (
      <motion.div
        style={{ border: "2px solid #d9f8eb", borderRadius: "10px" }}
        className="p-3 position-relative mt-4 mb-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y d-none d-md-block">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-info px-3 py-2">
            <span className="fs-11">Diagnosis</span>
          </span>
        </div>

        <div className="mt-4">
          {diagnosis.map((item, idx) => {
            return (
              <Card key={idx} className="mb-3 border-0 shadow-md">
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col xs={2} className="text-center">
                      <div
                        className="rounded-circle bg-info text-white d-flex align-items-center justify-content-center"
                        style={{ width: "36px", height: "36px" }}
                      >
                        {idx + 1}
                      </div>
                    </Col>

                    <Col xs={3}>
                      <div className="d-flex flex-column gap-1">
                        <small className="fs-10 fw-bold d-block">
                          Diagnosis Type
                        </small>
                        <strong className="fs-10 text-capitalize text-600">
                          {item?.diagnosis_type}
                        </strong>
                      </div>
                    </Col>

                    <Col xs={7}>
                      <div>
                        <small className="fs-10 d-block fw-bold">
                          Diagnosis Code Description
                        </small>
                        <div className="d-flex fs-10 text-600">
                          {item.diagnosis_code && (
                            <span className="pe-2">
                              ({item.diagnosis_code}) -
                            </span>
                          )}
                          <span>{item?.diagnosis_description}</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      </motion.div>
    );
  }, [isAdvanced, diagnosis]);

  //   // Render totals section

  const renderTotalsSection = useMemo(() => {
    const services = viewPreauthorizationInfo?.service_procedures;

    // If no services → don't show the totals card
    if (!services || !Array.isArray(services) || services.length === 0) {
      return null;
    }

    // Safely calculate totals (convert null/undefined/string to number)
    const totalEligible = services.reduce((sum, item) => {
      const amount = parseFloat(item.elig_amnt ?? 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    const totalBenefit = services.reduce((sum, item) => {
      const amount = parseFloat(item.benf_amnt ?? 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    const totalPatientShare = services.reduce((sum, item) => {
      const amount = parseFloat(item.prdpshre ?? 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    return (
      <Row className="mt-4">
        <Col xs={12}>
          <Card className="p-3 shadow-md border-0">
            <Row className="g-3">
              <Col xs={12} md={4}>
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">
                    Eligible Amount
                  </span>
                  <span className="fs-10 fw-bold text-primary">
                    {totalEligible.toFixed(2)} SAR
                  </span>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">Benefit Amount</span>
                  <span className="fs-10 fw-bold text-primary">
                    {totalBenefit.toFixed(2)} SAR
                  </span>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">Patient Share</span>
                  <span className="fs-10 fw-bold text-success">
                    {totalPatientShare.toFixed(2)} SAR
                  </span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    );
  }, [viewPreauthorizationInfo?.service_procedures]);

  return (
    <>
      <motion.div
        style={{ border: "2px solid #d9f8eb" }}
        className="rounded p-3 position-relative mt-3 mb-3"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.5 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y d-none d-md-block">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success px-3 py-2">
            <span className="fs-11">Service & Procedures</span>
          </span>
        </div>

        {!isAdvanced && renderServiceItems}
        {isAdvanced && renderAdvancedItems}
        {isAdvanced && renderDiagnosisSection}
        {!isAdvanced && renderTotalsSection}
      </motion.div>

      {/* {renderDiagnosisSection} */}
    </>
  );
};

export default PreauthResponseService;
